import { useNavigate } from 'react-router-dom';
import { useEffect, useState, type JSX } from 'react';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import { CollectionCard } from '@/components/ui';
import { getUserListsPreview } from '@/services/slaydAPI';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import { getUserName, isUserSigned } from '@/utils/getUserInfo';
import styles from './SlaylistPage.module.css';

const profileImg =
  'https://d19dlu1w9mnmln.cloudfront.net/uploads/6bf599fb-9cc3-48c9-9e10-d27b3410f753.webp';

interface TopProducts {
  id: string | number;
  imageUrl: string;
}

const SlaylistPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { slaylists, setSlaylists } = useSlaylistSelectorStore();
  const [empty, setEmpty] = useState<boolean>(false);

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      const authResult = isUserSigned();
      if (!authResult) {
        navigate('/');
      }
    };

    const fetchLists = async (): Promise<void> => {
      try {
        const lists = await getUserListsPreview();
        setSlaylists(lists || []);
      } catch (error) {
        console.error('Error:', error);
      }
    };
    checkAuth();
    fetchLists();
  }, [navigate, setSlaylists]); // no other dependencies

  useEffect(() => {
    function checkEmpty(): void {
      if (
        slaylists.length === 1 &&
        (slaylists[0]?.topProducts?.length === 0 || !slaylists[0]?.topProducts)
      ) {
        setEmpty(true);
      } else {
        setEmpty(false);
      }
    }

    checkEmpty();
  }, [slaylists]);

  return (
    <div className={styles.pageWrapper}>
      <button className={styles.backButton} onClick={() => navigate(-1)}>
        <img src={backButtonDark} alt="Back" className={styles.backIcon} />
      </button>
      <div className={styles.content}>
        <img src={profileImg} alt="Profile" className={styles.profileImg} />
        <div className={styles.title}>{getUserName()}&apos;s Slaylist</div>
        {!empty ? (
          ''
        ) : (
          <div className={styles.subtitle}>
            Your saved products will appear over here
          </div>
        )}
        {!empty && (
          <div className={styles.collectionCards}>
            {[
              ...slaylists.filter((list) => list.name === 'Saved'),
              ...slaylists.filter((list) => list.name !== 'Saved'),
            ].map((list) => {
              const transformedProducts =
                list.topProducts?.map((item: TopProducts) => ({
                  ...item,
                  id: Number(item.id) || 0,
                  imageUrl: item.imageUrl,
                })) || [];

              return (
                <CollectionCard
                  id={list.id}
                  products={transformedProducts}
                  key={list.id}
                  title={list.name === 'Saved' ? 'All Saves' : list.name}
                  totalSaves={list.totalProducts}
                  images={
                    list.topProducts?.slice(0, 3).map((item: TopProducts) => ({
                      id: Number(item.id) || 0,
                      url: item.imageUrl,
                    })) || []
                  }
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SlaylistPage;
